import { untrack } from "svelte"
import { fromStore } from "svelte/store"
import { editPostalRecipient } from "../_actions/editPostalRecipient"
import { createContactOrderPlanningProjection } from "./planningProjection.svelte"

import type { TableRowsUpdater } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
import type {
  ContactOrder,
  ContactOrderPlanningPreview,
  ContactOrderPostalSelection,
} from "../../../../../../api/kehrblatt/contact-orders"
import type { CostBenefitEstimate } from "../../../../../../api/kehrblatt/cost-benefit"
import { postalSelectionFromValue, postalSelectionValue } from "../../../_logic/channels"
import {
  type ContactOrderAppointmentRow,
  type ContactOrderDigitalChannel,
  type ContactOrderPlanningOperations,
  type ContactOrderPlanningRowPresentation,
  type ContactOrderPlanningSubmissionPhase,
  type ContactOrderPlanningSubmissionResult,
  type ContactOrderPlanningSurfacePayloadBuilder,
} from "../../../_logic/contact-orders-contract"
import { buildContactOrderBatchPayloads } from "../../../_logic/contact-orders-payload"
import { contactOrderTargetEvents } from "../../../_logic/create-result"
import { loadOrgBillingPlan, orgContactChannelCapabilities } from "../../../_logic/org-billing-plan"
import type { PreconfigurationChoice } from "../../../_logic/preconfiguration-keep"
import {
  communicationSelectionForPlanningRow,
  reconcileSelectionStateForRows,
  resetPlanSelectionState,
  toggleDigitalChannelSelectionState,
  type ContactOrderSelectionState,
} from "../../../_logic/selection-state"
import { loadAutomaticPreconfiguration } from "../_actions/loadAutomaticPreconfiguration"
import {
  applyContactOrderPreconfiguration,
  createContactOrderDigitalChannelChanges,
  type ContactOrderDigitalPreset,
  type ContactOrderPostalPreset,
} from "../_shared/_logic/preconfiguration"
import type { QuickRecipientSaveResult } from "../_shared/_logic/recipient-quick-edit-contract"
import { createContactOrderPlanningFilters, type ContactOrderPlanningFilterState } from "./filters"
import { ContactOrderPlanningDraft } from "./planningDraft.svelte"
import { notifyPreconfigurationApplyResult } from "./preconfigurationApplyFeedback"
import {
  createContactOrderPreviewController,
  type ContactOrderPreviewController,
} from "./previewController"
import { mergeById, workOrdersForRecipientScope } from "./recipientQuickEditState"
import { contactOrderPlanningEventAllowsInteraction } from "./rowEligibility"
import { runContactOrderPlanningSubmissionTransition } from "./submissionTransition"

type EmployeeOption = { label: string; value: string }

import type { PlanningRendererPorts } from "../_actions/openPlanningEditors"
export type ContactOrderPlanningProps<TCommitReceipt = undefined> = {
  organizationId: string
  planningDraft?: ContactOrderPlanningDraft
  events: ScheduleEvent[]
  workOrders: WorkOrder[]
  properties: Property[]
  customers: Customer[]
  contactPoints: ContactPoint[]
  employeeOptions: EmployeeOption[]
  contactOrders?: ContactOrder[]
  caseIdByOrderId?: Record<string, string | null>
  initialFilterStatus?: ContactOrderPlanningFilterState["status"]
  rowPresentationByEventId?: Record<string, ContactOrderPlanningRowPresentation>
  initialRemovedEventIds?: string[]
  allowRemoveOnlySubmission?: boolean
  locked?: boolean
  readOnly?: boolean
  createButtonLabel?: string
  continueButtonLabel?: string
  backButtonLabel?: string
  showContinueButton?: boolean
  helpModalTextId?: string
  successMessage?: (createdCount: number) => string
  buildPayloads?: ContactOrderPlanningSurfacePayloadBuilder
  operations: ContactOrderPlanningOperations<TCommitReceipt>
  loading?: boolean
  onSubmissionResult: (
    result: ContactOrderPlanningSubmissionResult<TCommitReceipt>,
  ) => void | Promise<void>
  onRowsRemoved?: (eventIds: string[]) => void | Promise<void>
  onRowsRestored?: (eventIds: string[]) => void | Promise<void>
  onBack?: () => void
  setError?: (message: string | null) => void
}

export function createContactOrderPlanningController<TCommitReceipt>(
  source: () => ContactOrderPlanningProps<TCommitReceipt>,
  ports: PlanningRendererPorts,
) {
  const organizationId = $derived(source().organizationId)
  const planningDraft = $derived(source().planningDraft)
  const events = $derived(source().events)
  const workOrders = $derived(source().workOrders)
  const properties = $derived(source().properties)
  const customers = $derived(source().customers)
  const contactPoints = $derived(source().contactPoints)
  const employeeOptions = $derived(source().employeeOptions)
  const contactOrders = $derived(source().contactOrders ?? [])
  const caseIdByOrderId = $derived(source().caseIdByOrderId ?? {})
  const initialFilterStatus = $derived(source().initialFilterStatus ?? "pending")
  const rowPresentationByEventId = $derived(source().rowPresentationByEventId ?? {})
  const initialRemovedEventIds = $derived(source().initialRemovedEventIds ?? [])
  const allowRemoveOnlySubmission = $derived(source().allowRemoveOnlySubmission ?? false)
  const locked = $derived(source().locked ?? false)
  const readOnly = $derived(source().readOnly ?? false)
  const createButtonLabel = $derived(source().createButtonLabel ?? "Ankündigungspläne anlegen")
  const successMessage = $derived(
    source().successMessage ??
      ((createdCount: number) => `Ankündigungspläne angelegt: ${createdCount}.`),
  )
  const buildPayloads = $derived(source().buildPayloads ?? buildContactOrderBatchPayloads)
  const operations = $derived(source().operations)
  const loading = $derived(source().loading ?? false)
  const onSubmissionResult = $derived(source().onSubmissionResult)
  const onRowsRemoved = $derived(source().onRowsRemoved)
  const onRowsRestored = $derived(source().onRowsRestored)
  const setError = $derived(source().setError ?? (() => {}))
  const initialState = untrack(() => ({
    localEvents: events,
    localWorkOrders: workOrders,
    localCustomers: customers,
    localContactOrders: contactOrders,
    initialFilterStatus,
    initialRemovedEventIds,
  }))

  let localEvents = $state<ScheduleEvent[]>(initialState.localEvents)
  let localWorkOrders = $state<WorkOrder[]>(initialState.localWorkOrders)
  let localCustomers = $state<Customer[]>(initialState.localCustomers)
  let localContactPointUpdates = $state<ContactPoint[]>([])
  let deletedContactPointIds = $state<string[]>([])
  let localContactOrders = $state<ContactOrder[]>(initialState.localContactOrders)
  let submissionPhase = $state<ContactOrderPlanningSubmissionPhase>("idle")
  const draft = untrack(
    () =>
      planningDraft ??
      new ContactOrderPlanningDraft(organizationId, initialState.initialRemovedEventIds),
  )
  const selectionState = $derived(draft.selectionState)
  let dirtyScheduleEventIds = $state<Record<string, true>>({})
  let planningPreviewByEventId = $state<Record<string, ContactOrderPlanningPreview>>({})
  let planningPreviewErrorsByEventId = $state<Record<string, string>>({})
  let planningCostBenefit = $state<CostBenefitEstimate | null>(null)
  let planningInitialPreviewLoading = $state(false)
  let planningPreviewPendingEventIds = $state<string[]>([])
  let planningPreviewAttempted = $state(false)
  let contactFilters = $state<ContactOrderPlanningFilterState>(
    createContactOrderPlanningFilters({ status: initialState.initialFilterStatus }),
  )
  // A bulk edit changes only the fields the user expressly selects here.
  let digitalPreset = $state<PreconfigurationChoice<ContactOrderDigitalPreset>>("__keep__")
  let digitalChannelChanges = $state(createContactOrderDigitalChannelChanges())
  let postalPreset = $state<PreconfigurationChoice<ContactOrderPostalPreset>>("__keep__")
  let preconfigurationPending = $state(false)
  let selectedIds = $state<string[]>([])
  const removedEventIds = $derived(draft.removedEventIds)
  let planningPreviewController: ContactOrderPreviewController | null = null
  let updateTableRows = $state<TableRowsUpdater | null>(null)
  let active = true
  // Kept for the lifetime of this draft, including an uncertain HTTP outcome.
  const contextActive = () => active && draft.valid && organizationId === draft.organizationId
  const buildCommitPayloads: ContactOrderPlanningSurfacePayloadBuilder = (input) =>
    buildPayloads({ ...input, confirmPreviewSelection: true }).map((payload) => {
      return {
        ...payload,
        planning_operation_id: draft.operationIdFor(
          payload.work_order_id,
          payload.schedule_event_id,
        ),
      }
    })

  const channelCapabilities = fromStore(orgContactChannelCapabilities)
  const allowedContactChannels = $derived(channelCapabilities.current(organizationId))
  const projection = createContactOrderPlanningProjection(() => ({
    organizationId,
    draft,
    localEvents: contextActive() ? localEvents : [],
    localWorkOrders,
    localCustomers,
    localContactOrders,
    contactPoints,
    localContactPointUpdates,
    deletedContactPointIds,
    properties,
    employeeOptions,
    planningPreviewByEventId,
    planningPreviewErrorsByEventId,
    dirtyScheduleEventIds,
    rowPresentationByEventId,
    selectionState,
    removedEventIds,
    selectedIds,
    contactFilters,
    allowedContactChannels,
    planningPreviewPendingEventIds,
    planningInitialPreviewLoading,
    planningCostBenefit,
    planningPreviewAttempted,
    loading,
    readOnly,
    locked,
    preconfigurationPending,
    submissionPhase,
    caseIdByOrderId,
    allowRemoveOnlySubmission,
    buildCommitPayloads,
    createButtonLabel,
    successMessage,
  }))
  const pendingContactOrders = $derived(projection.pendingContactOrders)
  const totalCount = $derived(projection.totalCount)
  const contactStepLocked = $derived(projection.contactStepLocked)
  const submissionPending = $derived(projection.submissionPending)
  const tableLoading = $derived(projection.tableLoading)
  const employeeLabelById = $derived(projection.employeeLabelById)
  const workOrderById = $derived(projection.workOrderById)
  const propertyById = $derived(projection.propertyById)
  const customersById = $derived(projection.customersById)
  const localContactPoints = $derived(projection.localContactPoints)
  const contactPointsByCustomerId = $derived(projection.contactPointsByCustomerId)
  const appointmentRows = $derived(projection.appointmentRows)
  const isEventRemoved = $derived(projection.isEventRemoved)
  const isRowRemoved = $derived(projection.isRowRemoved)
  const rowIsInteractionDisabled = $derived(projection.rowIsInteractionDisabled)
  const isConfirmationInformationRow = $derived(projection.isConfirmationInformationRow)
  const displayAppointmentRows = $derived(projection.displayAppointmentRows)
  const communicationSelectionForRow = $derived(projection.communicationSelectionForRow)
  const visibleAppointmentRows = $derived(projection.visibleAppointmentRows)
  const actionableSelectedIds = $derived(projection.actionableSelectedIds)
  const tableRows = $derived(projection.tableRows)
  const digitalChannelTogglesForRow = $derived(projection.digitalChannelTogglesForRow)
  const postalOptionsForRow = $derived(projection.postalOptionsForRow)
  const configuredPostalSelectionForRow = $derived(projection.configuredPostalSelectionForRow)
  const pendingRows = $derived(projection.pendingRows)
  const applyTargetResolution = $derived(projection.applyTargetResolution)
  const skippedPendingCount = $derived(projection.skippedPendingCount)
  const skippedWarningText = $derived(projection.skippedWarningText)
  const createIntent = $derived(projection.createIntent)
  const filterFields = $derived(projection.filterFields)
  const showMetrics = $derived(projection.showMetrics)
  const costBenefitLoading = $derived(projection.costBenefitLoading)
  const costBenefitError = $derived(projection.costBenefitError)
  const appointmentsRefresh = $derived(projection.appointmentsRefresh)
  const isAnnouncementPlanPending = $derived(projection.isAnnouncementPlanPending)

  const setSelectionState = (next: ContactOrderSelectionState) => {
    if (contextActive()) draft.selectionState = next
  }

  $effect(() => {
    const nextRows = tableRows
    const updater = updateTableRows

    if (updater && contextActive())
      untrack(() => updater(() => nextRows, { total: nextRows.length }))
  })

  const buildContactOrderPayloadsForEvents = (
    targetEvents: ScheduleEvent[],
    includeInactiveRows = false,
  ) =>
    buildPayloads({
      targetEvents,
      appointmentRows,
      caseIdByOrderId,
      communicationSelectionByEventId: selectionState.communicationSelectionByEventId,
      allowedContactChannels,
      includeInactiveRows,
    })

  planningPreviewController = createContactOrderPreviewController({
    getTargetEvents: () => {
      if (loading) return []

      return contactOrderTargetEvents({
        pendingContactOrders,
        appointmentRows,
        selectionState,
        allowedContactChannels,
        includeAutomaticRows: true,
      }).filter((event) => !isEventRemoved(event.id))
    },
    buildPayloads: (targetEvents) => buildContactOrderPayloadsForEvents(targetEvents, true),
    loadPreview: (payloads) => operations.previewContactOrders(payloads),
    setPreviewByEventId: (next) => {
      if (!contextActive()) return

      planningPreviewByEventId = next
      const selections = { ...selectionState.communicationSelectionByEventId }
      for (const row of appointmentRows) {
        if (row.planningPreview && !row.planningPreviewError && !selections[row.event.id]) {
          selections[row.event.id] = communicationSelectionForPlanningRow(
            row,
            selectionState,
            allowedContactChannels,
          )
        }
      }
      draft.selectionState = { communicationSelectionByEventId: selections }
      setSelectionState(
        reconcileSelectionStateForRows(appointmentRows, selectionState, allowedContactChannels),
      )
    },
    setErrorsByEventId: (next) => {
      if (!contextActive()) return

      planningPreviewErrorsByEventId = next
    },
    setCostBenefit: (next) => {
      if (!contextActive()) return

      planningCostBenefit = next
    },
    setInitialLoading: (next) => {
      if (!contextActive()) return

      if (next) planningPreviewAttempted = true

      planningInitialPreviewLoading = next
    },
    setPendingEventIds: (next) => {
      if (!contextActive()) return

      planningPreviewPendingEventIds = next
    },
  })

  const schedulePlanningPreviewForRows = (rows: ContactOrderAppointmentRow[], delay = 220) => {
    planningPreviewController?.queueForEvents(
      rows
        .map((row) => row.event)
        .filter(
          (event) =>
            event.work_order_id &&
            !event.contact_order_id &&
            !isEventRemoved(event.id) &&
            contactOrderPlanningEventAllowsInteraction(event.id, rowPresentationByEventId),
        ),
      delay,
    )
  }

  const schedulePlanningPreviewForWorkOrderIds = (workOrderIds: string[], delay = 220) => {
    const targetWorkOrderIds = new Set(workOrderIds)
    planningPreviewController?.queueForEvents(
      pendingContactOrders.filter(
        (event) =>
          event.work_order_id &&
          targetWorkOrderIds.has(event.work_order_id) &&
          !isEventRemoved(event.id),
      ),
      delay,
    )
  }

  const handleRecipientQuickEditSaved = async (
    workOrder: WorkOrder,
    result: QuickRecipientSaveResult,
  ) => {
    if (!contextActive()) return

    const changedCustomerIds = [
      ...result.customers.map((customer) => customer.id),
      ...result.contactPoints.flatMap((point) => (point.customer_id ? [point.customer_id] : [])),
      ...localContactPoints.flatMap((point) =>
        result.deletedContactPointIds.includes(point.id) && point.customer_id
          ? [point.customer_id]
          : [],
      ),
    ]
    const affectedWorkOrders = workOrdersForRecipientScope(
      localWorkOrders,
      workOrder,
      changedCustomerIds,
    )
    const affectedWorkOrderIds = affectedWorkOrders.map((entry) => entry.id)

    if (result.customers.length) localCustomers = mergeById(localCustomers, result.customers)

    if (result.contactPoints.length)
      localContactPointUpdates = mergeById(localContactPointUpdates, result.contactPoints)

    deletedContactPointIds = [
      ...new Set([...deletedContactPointIds, ...result.deletedContactPointIds]),
    ]
    planningPreviewController?.invalidateForEvents(
      localEvents.filter(
        (event) => event.work_order_id && affectedWorkOrderIds.includes(event.work_order_id),
      ),
    )
    const reloaded = await ports.reloadRecipientWorkOrders({
      organizationId,
      workOrderIds: affectedWorkOrderIds,
    })

    if (!contextActive()) return

    if (reloaded.length) localWorkOrders = mergeById(localWorkOrders, reloaded)

    if (!contextActive()) return

    setSelectionState(
      reconcileSelectionStateForRows(appointmentRows, selectionState, allowedContactChannels),
    )
    schedulePlanningPreviewForWorkOrderIds(affectedWorkOrderIds, 0)
  }

  const openRecipientQuickEditForRow = (row: ContactOrderAppointmentRow, event?: MouseEvent) => {
    event?.stopPropagation()

    if (
      tableLoading ||
      contactStepLocked ||
      submissionPending ||
      rowIsInteractionDisabled(row) ||
      isRowRemoved(row) ||
      isConfirmationInformationRow(row)
    )
      return

    const workOrder = row.event.work_order_id
      ? (workOrderById[row.event.work_order_id] ?? null)
      : null
    ports.openRecipientQuickEdit({
      organizationId,
      row,
      workOrder,
      customers: localCustomers,
      contactPoints: localContactPoints,
      onSaved: (result: QuickRecipientSaveResult) =>
        handleRecipientQuickEditSaved(workOrder!, result),
    })
  }

  const mount = () => {
    void loadOrgBillingPlan(organizationId)
    planningPreviewController?.mount()
  }
  const destroy = () => {
    active = false
    planningPreviewController?.destroy()
    updateTableRows = null
  }

  $effect(() => {
    // Context invalidation cancels external work; it is not draft/prop synchronization.
    if (organizationId !== draft.organizationId) {
      untrack(() => {
        draft.invalidate()
        destroy()
      })
    }
  })

  const applyPreconfigurationToRows = async () => {
    if (tableLoading || contactStepLocked || submissionPending) return

    const targetRows = applyTargetResolution.targetRows
    const capturedState = selectionState
    const selectedDigitalPreset = digitalPreset
    const selectedPostalPreset = postalPreset
    let automaticSelectionsByEventId

    if (selectedDigitalPreset === "auto") {
      preconfigurationPending = true
      try {
        automaticSelectionsByEventId = await loadAutomaticPreconfiguration({
          rows: targetRows,
          buildPayloads,
          previewContactOrders: operations.previewContactOrders,
          caseIdByOrderId,
          allowedContactChannels,
        })
      } catch {
        if (contextActive())
          notifyPreconfigurationApplyResult({
            total: targetRows.length,
            applied: 0,
            reason:
              "Die automatische Kontaktwegeauswahl konnte nicht geladen werden. Bitte erneut anwenden.",
          })

        return
      } finally {
        preconfigurationPending = false
      }

      if (!contextActive() || contactStepLocked) return

      const currentEvents = new Set(
        pendingRows
          .filter((row) => !isRowRemoved(row) && !isConfirmationInformationRow(row))
          .map((row) => row.event),
      )

      if (
        selectionState !== capturedState ||
        targetRows.some((row) => !currentEvents.has(row.event))
      ) {
        notifyPreconfigurationApplyResult({
          total: targetRows.length,
          applied: 0,
          reason: "Die Zeilen wurden inzwischen aktualisiert. Bitte erneut anwenden.",
        })

        return
      }
    }

    const result = applyContactOrderPreconfiguration({
      targetRows,
      digitalPreset: selectedDigitalPreset,
      digitalChannelChanges,
      postalPreset: selectedPostalPreset,
      automaticSelectionsByEventId,
      state: selectionState,
      allowedContactChannels,
    })
    setSelectionState(result.state)
    notifyPreconfigurationApplyResult({
      total: targetRows.length,
      applied: result.applied,
      skipped: result.skipped,
      reasonCounts: result.skippedReasons,
      reason:
        "Passende Ansprechpartner, Kontaktwege oder die gewählte Zustellart sind in diesen Zeilen nicht verfügbar. Diese Zeilen wurden nicht verändert.",
    })
    schedulePlanningPreviewForRows(result.appliedRows)
  }

  const updateFilter = (fieldId: string, value: unknown) => {
    contactFilters = {
      ...contactFilters,
      [fieldId]: String(value ?? ""),
    }
  }

  const toggleDigitalChannel = (
    row: ContactOrderAppointmentRow,
    customerId: string,
    channel: ContactOrderDigitalChannel,
    event?: MouseEvent,
  ) => {
    event?.stopPropagation()

    if (isConfirmationInformationRow(row)) return

    if (tableLoading || contactStepLocked || submissionPending || rowIsInteractionDisabled(row))
      return

    setError(null)
    const nextSelectionState = toggleDigitalChannelSelectionState({
      row,
      customerId,
      channel,
      state: selectionState,
      allowedContactChannels,
    })

    if (nextSelectionState === selectionState) return

    setSelectionState(nextSelectionState)
    schedulePlanningPreviewForRows([row])
  }

  const selectPostal = (
    row: ContactOrderAppointmentRow,
    value: string,
    dispatch: "MANUAL" | "AUTOMATIC",
  ) => {
    if (isConfirmationInformationRow(row)) return

    if (tableLoading || contactStepLocked || submissionPending || rowIsInteractionDisabled(row))
      return

    if (dispatch === "AUTOMATIC" && !row.planningPreview?.automatic_postal_available) return

    if (value === "INDIVIDUAL") {
      const existing = configuredPostalSelectionForRow(row)

      if (existing.manual_recipient) {
        updatePostalDraft(row, { ...existing, dispatch_mode: dispatch })
      } else {
        openPostalEditor(row, true, dispatch)
      }

      return
    }

    const current = configuredPostalSelectionForRow(row)
    const postal = postalSelectionFromValue(value, postalOptionsForRow(row))

    if (!postal) return

    updatePostalDraft(row, {
      ...postal,
      ...(postalSelectionValue(current) === value && current.recipient_name
        ? { recipient_name: current.recipient_name }
        : {}),
      dispatch_mode: postal.mode === "PERSONAL" ? dispatch : "MANUAL",
    })
  }

  const updatePostalDraft = (
    row: ContactOrderAppointmentRow,
    postal: ContactOrderPostalSelection,
    enabled = communicationSelectionForRow(row).postal.mode !== "DISABLED",
  ) => {
    if (
      !active ||
      tableLoading ||
      contactStepLocked ||
      submissionPending ||
      rowIsInteractionDisabled(row) ||
      isRowRemoved(row)
    )
      return

    setError(null)
    draft.configurePostal(String(row.id), postal, enabled, communicationSelectionForRow(row))
    schedulePlanningPreviewForRows([row])
  }

  const togglePostal = (row: ContactOrderAppointmentRow) => {
    const enabled = communicationSelectionForRow(row).postal.mode === "DISABLED"
    const postal = configuredPostalSelectionForRow(row)

    if (
      isConfirmationInformationRow(row) ||
      postal.mode === "DISABLED" ||
      (enabled &&
        postal.dispatch_mode === "AUTOMATIC" &&
        !row.planningPreview?.automatic_postal_available)
    )
      return

    updatePostalDraft(row, postal, enabled)
  }

  const openPostalEditor = (
    row: ContactOrderAppointmentRow,
    individual = false,
    dispatch?: "MANUAL" | "AUTOMATIC",
  ) => {
    const editable = (currentRow: ContactOrderAppointmentRow | undefined) =>
      Boolean(
        contextActive() &&
        currentRow &&
        !tableLoading &&
        !contactStepLocked &&
        !submissionPending &&
        !rowIsInteractionDisabled(currentRow) &&
        !isRowRemoved(currentRow),
      )

    if (!editable(row)) return

    const order = workOrderById[row.event.work_order_id ?? ""]
    editPostalRecipient({
      organizationId,
      row,
      individual,
      dispatch,
      original: configuredPostalSelectionForRow(row),
      enabled: communicationSelectionForRow(row).postal.mode !== "DISABLED",
      property: propertyById[order?.property_id ?? ""],
      open: ports.openPostalRecipientEditor,
      current: () => {
        const currentRow = appointmentRows.find((entry) => entry.id === row.id)

        return {
          organizationId,
          row: currentRow,
          editable: editable(currentRow),
          selection: currentRow
            ? configuredPostalSelectionForRow(currentRow)
            : { mode: "DISABLED" },
          enabled: Boolean(
            currentRow && communicationSelectionForRow(currentRow).postal.mode !== "DISABLED",
          ),
        }
      },
      apply: updatePostalDraft,
    })
  }

  const disablePlansForRows = (rows: ContactOrderAppointmentRow[]) => {
    if (tableLoading || contactStepLocked || submissionPending) return

    const removableRows = rows.filter((row) => !isRowRemoved(row) && !rowIsInteractionDisabled(row))

    if (!removableRows.length) return

    setError(null)
    draft.removedEventIds = Array.from(
      new Set([...removedEventIds, ...removableRows.map((row) => row.event.id)]),
    )
    const removedRowIds = new Set(removableRows.map((row) => String(row.id)))
    selectedIds = selectedIds.filter((id) => !removedRowIds.has(id))
    planningPreviewController?.recomputeCostBenefit()
  }

  const disablePlanForRow = (row: ContactOrderAppointmentRow, event?: MouseEvent) => {
    event?.stopPropagation()
    disablePlansForRows([row])
  }

  const enablePlanForRow = async (row: ContactOrderAppointmentRow, event?: MouseEvent) => {
    event?.stopPropagation()

    if (tableLoading || contactStepLocked || submissionPending || rowIsInteractionDisabled(row))
      return

    if (!isRowRemoved(row)) return

    setError(null)
    await onRowsRestored?.([row.event.id])

    if (!contextActive()) return

    draft.removedEventIds = removedEventIds.filter((id) => id !== row.event.id)
    planningPreviewController?.recomputeCostBenefit()
    schedulePlanningPreviewForRows([row])
  }

  const resetPlansForRows = (rows: ContactOrderAppointmentRow[]) => {
    if (tableLoading || contactStepLocked || submissionPending) return

    const resettableRows = rows.filter(
      (row) =>
        !rowIsInteractionDisabled(row) && !isRowRemoved(row) && !isConfirmationInformationRow(row),
    )

    if (!resettableRows.length) return

    setError(null)
    let nextSelectionState = selectionState
    for (const row of resettableRows) {
      nextSelectionState = resetPlanSelectionState(row, nextSelectionState)
    }
    setSelectionState(nextSelectionState)
    schedulePlanningPreviewForRows(resettableRows)
  }

  const buildContinueResult = (dirtyEventsPersisted: boolean) => ({
    createdContactOrders: localContactOrders,
    scheduleEvents: localEvents,
    dirtyEventsPersisted,
  })

  const handleSubmissionResult = (result: ContactOrderPlanningSubmissionResult<TCommitReceipt>) => {
    if (!contextActive()) return Promise.resolve()

    return runContactOrderPlanningSubmissionTransition({
      result,
      onRowsRemoved: (ids) => {
        if (contextActive()) return onRowsRemoved?.(ids)
      },
      onCommitted: (committed) => {
        if (!contextActive()) return

        localEvents = committed.result.scheduleEvents
        localContactOrders = committed.result.createdContactOrders
        setError(
          committed.status === "partial"
            ? "Einige Ankündigungspläne konnten nicht angelegt werden."
            : committed.status === "empty"
              ? "Keine Kundeninformation abgeschlossen."
              : null,
        )
      },
      onSubmissionResult: (next) => {
        if (contextActive()) return onSubmissionResult(next)
      },
    })
  }

  return {
    get tableLoading() {
      return tableLoading
    },
    get preconfigurationPending() {
      return preconfigurationPending
    },
    get submissionPhase() {
      return submissionPhase
    },
    get submissionPending() {
      return submissionPending
    },
    get createIntent() {
      return createIntent
    },
    get totalCount() {
      return totalCount
    },
    get appointmentRows() {
      return appointmentRows
    },
    get visibleAppointmentRows() {
      return visibleAppointmentRows
    },
    get contactStepLocked() {
      return contactStepLocked
    },
    get showMetrics() {
      return showMetrics
    },
    get planningCostBenefit() {
      return planningCostBenefit
    },
    get costBenefitLoading() {
      return costBenefitLoading
    },
    get costBenefitError() {
      return costBenefitError
    },
    get skippedPendingCount() {
      return skippedPendingCount
    },
    get skippedWarningText() {
      return skippedWarningText
    },
    get actionableSelectedIds() {
      return actionableSelectedIds
    },
    get applyTargetResolution() {
      return applyTargetResolution
    },
    get digitalPreset() {
      return digitalPreset
    },
    get digitalChannelChanges() {
      return digitalChannelChanges
    },
    get postalPreset() {
      return postalPreset
    },
    get filterFields() {
      return filterFields
    },
    get tableRows() {
      return tableRows
    },
    get appointmentsRefresh() {
      return appointmentsRefresh
    },
    get selectedIds() {
      return selectedIds
    },
    set submissionPhase(value) {
      submissionPhase = value
    },
    set digitalPreset(value) {
      digitalPreset = value
    },
    set digitalChannelChanges(value) {
      digitalChannelChanges = value
    },
    set postalPreset(value) {
      postalPreset = value
    },
    set selectedIds(value) {
      selectedIds = value
    },
    buildContinueResult,
    handleSubmissionResult,
    applyPreconfigurationToRows,
    updateFilter,
    get digitalChannelTogglesForRow() {
      return digitalChannelTogglesForRow
    },
    get communicationSelectionForRow() {
      return communicationSelectionForRow
    },
    get configuredPostalSelectionForRow() {
      return configuredPostalSelectionForRow
    },
    get postalOptionsForRow() {
      return postalOptionsForRow
    },
    get isAnnouncementPlanPending() {
      return isAnnouncementPlanPending
    },
    toggleDigitalChannel,
    selectPostal,
    togglePostal,
    openPostalEditor,
    disablePlanForRow,
    enablePlanForRow,
    disablePlansForRows,
    resetPlansForRows,
    openRecipientQuickEditForRow,
    mount,
    destroy,
    bindRowsUpdater: (updater: TableRowsUpdater) => {
      updateTableRows = updater
    },
    retryPreview: async (row: ContactOrderAppointmentRow) => {
      if (!contextActive() || contactStepLocked || submissionPending || isRowRemoved(row)) return

      const revision = draft.revisionFor(String(row.id))
      await planningPreviewController?.refreshForEvents([row.event])

      if (
        contextActive() &&
        !isRowRemoved(row) &&
        revision === draft.revisionFor(String(row.id)) &&
        planningPreviewErrorsByEventId[String(row.id)]
      ) {
        await planningPreviewController?.refreshFactsForEvents([row.event])
      }
    },
  }
}
